import { Injectable } from "@angular/core";
import { Action, Selector, State, StateContext } from "@ngxs/store";
import { GetProgramsProgram } from "@platform-app/app/core/api/models/get-programs-program";
import { finalize, Observable, switchMap, tap } from "rxjs";
import { SchoolService } from "@platform-app/app/core/api/services/school.service";
import { GetProgramsResponse } from "@platform-app/app/core/api/models/get-programs-response";
import { StateToDefaultResetter } from "@platform-app/app/shared/common/store/state-to-default-resetter/state-to-default-resetter";
import {
  GetOrganizationPrograms,
  DeleteOrganizationProgram,
  AddOrganizationProgram,
  UpdateOrganizationProgram,
  PageRangeChanged,
  PageNumberChanged,
} from "@platform-app/app/main/organization-settings-page/programs-page/programs-page.actions";

export interface ProgramsPageStateModel {
  programs: GetProgramsProgram[];
  programsTotalAmount: number;
  currentPage: number;
  itemsPerPage: number;
  isLoading: boolean;
}

const DEFAULT_PROGRAMS_STATE: ProgramsPageStateModel = {
  programs: [],
  programsTotalAmount: 0,
  currentPage: 1,
  itemsPerPage: 10,
  isLoading: false,
};

@State<ProgramsPageStateModel>({
  name: "OrganizationProgramsPage",
  defaults: DEFAULT_PROGRAMS_STATE,
})
@Injectable()
export class ProgramsPageState extends StateToDefaultResetter<ProgramsPageStateModel> {
  constructor(private schoolService: SchoolService) {
    super(DEFAULT_PROGRAMS_STATE);
  }

  @Selector()
  static isLoading(state: ProgramsPageStateModel) {
    return state.isLoading;
  }

  @Selector()
  static programs(state: ProgramsPageStateModel) {
    return state.programs;
  }

  @Selector()
  static itemsPerPage(state: ProgramsPageStateModel) {
    return state.itemsPerPage;
  }

  @Selector()
  static currentPage(state: ProgramsPageStateModel) {
    return state.currentPage;
  }

  @Selector()
  static programsTotalAmount(state: ProgramsPageStateModel) {
    return state.programsTotalAmount;
  }

  @Action(GetOrganizationPrograms)
  getOrganizationPrograms(
    ctx: StateContext<ProgramsPageStateModel>,
    action: GetOrganizationPrograms,
  ) {
    return this.getOrganizationProgramsRequest(action.payload.schoolId, ctx);
  }

  @Action(DeleteOrganizationProgram)
  deleteOrganizationProgram(
    ctx: StateContext<ProgramsPageStateModel>,
    action: DeleteOrganizationProgram,
  ) {
    ctx.patchState({ isLoading: true });

    return this.schoolService
      .deleteProgram({
        schoolId: action.payload.schoolId,
        programId: action.payload.programId,
      })
      .pipe(
        switchMap(() =>
          this.getOrganizationProgramsRequest(action.payload.schoolId, ctx),
        ),
        finalize(() => ctx.patchState({ isLoading: false })),
      );
  }

  @Action(AddOrganizationProgram)
  addOrganizationProgram(
    ctx: StateContext<ProgramsPageStateModel>,
    action: AddOrganizationProgram,
  ) {
    ctx.patchState({ isLoading: true });

    return this.schoolService
      .addProgram({
        schoolId: action.payload.schoolId,
        body: action.payload.program,
      })
      .pipe(
        switchMap(() =>
          this.getOrganizationProgramsRequest(action.payload.schoolId, ctx),
        ),
        finalize(() => ctx.patchState({ isLoading: false })),
      );
  }

  @Action(UpdateOrganizationProgram)
  updateOrganizationProgram(
    ctx: StateContext<ProgramsPageStateModel>,
    action: UpdateOrganizationProgram,
  ) {
    ctx.patchState({ isLoading: true });

    return this.schoolService
      .updateProgram({
        schoolId: action.payload.schoolId,
        programId: action.payload.programId,
        body: action.payload.program,
      })
      .pipe(
        switchMap(() =>
          this.getOrganizationProgramsRequest(action.payload.schoolId, ctx),
        ),
        finalize(() => ctx.patchState({ isLoading: false })),
      );
  }

  @Action(PageRangeChanged)
  pageRangeChanged(
    ctx: StateContext<ProgramsPageStateModel>,
    action: PageRangeChanged,
  ) {
    ctx.patchState({
      itemsPerPage: action.payload.itemsPerPage,
      currentPage: 1,
    });

    return this.getOrganizationProgramsRequest(action.payload.schoolId, ctx);
  }

  @Action(PageNumberChanged)
  pageNumberChanged(
    ctx: StateContext<ProgramsPageStateModel>,
    action: PageNumberChanged,
  ) {
    ctx.patchState({
      currentPage: action.payload.pageNumber,
    });

    return this.getOrganizationProgramsRequest(action.payload.schoolId, ctx);
  }

  private getOrganizationProgramsRequest(
    schoolId: string,
    ctx: StateContext<ProgramsPageStateModel>,
  ): Observable<GetProgramsResponse> {
    ctx.patchState({ isLoading: true });

    const stateSnapshot = ctx.getState();

    return this.schoolService
      .getPrograms({
        schoolId,
        body: {
          pagination: {
            page: stateSnapshot.currentPage,
            itemsPerPage: stateSnapshot.itemsPerPage,
          },
        },
      })
      .pipe(
        tap((response) =>
          ctx.patchState({
            programs: response.programs,
            programsTotalAmount: response.totalCount,
          }),
        ),
        finalize(() => ctx.patchState({ isLoading: false })),
      );
  }
}
